import {
  createRef,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import {
  useBjelinTranslations,
  useProductTranslations
} from "../../hooks/TranslationHooks";
import { Link, useNavigate } from "react-router-dom";
import BjelinButton from "../BjelinButton";
import { productIsPurchasable, useProductUrl } from "../../utils/ProductUtils";
import IconsOrTagline from "../IconsOrTagline";
import { useSiteFeatures } from "../../hooks/useSiteFeatures";
import { Input } from "reactstrap";
import classNames from "classnames";
import PackageCalculator from "../PackageCalculator";
import { AnimatePresence, motion } from "motion/react";
import FormattedPrice from "../FormattedPrice";
import { addVat } from "../../utils/VatUtils";
import Icon from "../Icon";
import StockStatus from "../StockStatus";
import upperFirst from "lodash/upperFirst";
import { Flags } from "../Flags";
import { Product } from "../../types/Product";
import { ProductCategory } from "../../types/ProductCategory";
import "./style.scss";
import { addNoOverlayState } from "../../utils/UrlUtils";
import {
  useProductCartHandler,
  useProductPrice,
  useProductSpecsExtended,
  useProductStockStatus
} from "../../hooks/ProductHooks";

const ProductListItem = forwardRef<
  { focusInput: (value?: number) => void },
  {
    product: Product;
    preferredCategory?: ProductCategory;
    layout?: "card" | "list";
    searchResultMode?: boolean;
    showCartButton?: boolean;
    dontExpandCart?: boolean;
    noSlideShow?: boolean;
    noOverlay?: boolean;
    pkey: string;
    clickToFilter?: boolean;
    b2b?: boolean;
    compact?: boolean;
    active?: boolean;
    closeParent: () => void;
    focusSearchBox: () => void;
    setActive: (sku: string) => void;
    className?: string;
    showPricesWithVat?: boolean;
  }
>((props, ref) => {
  ref = ref || createRef();
  const {
    product,
    preferredCategory,
    b2b = false,
    searchResultMode = false,
    showCartButton = false,
    noOverlay = false,
    compact = false,
    active = false,
    closeParent,
    focusSearchBox,
    setActive,
    className = "",
    showPricesWithVat
  } = props;
  const features = useSiteFeatures();
  const tp = useProductTranslations();
  const t = useBjelinTranslations();
  const productUrl = useProductUrl(product, preferredCategory);
  const [amount, setAmount] = useState<number>(1);
  const [amountM2, setAmountM2] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const {
    hasPrice,
    basePrice,
    unitPrice,
    priceUnit,
    isPerSquareMeter,
    currency,
    unitDisplayPrice,
    displayPrice
  } = useProductPrice(product, b2b);
  const stockStatus = useProductStockStatus(product, tp, b2b);
  const { iconSpecsIndexed } = useProductSpecsExtended(product);
  const { inCart, addToCartHandler } = useProductCartHandler(
    product,
    amount,
    false,
    b2b
  );

  const { name, tagline, images, unitQuantityFactor = 0, sku, stock } = product;

  function focusInput(value?: number) {
    if (inputRef.current) {
      if (value) {
        if (isPerSquareMeter) {
          setAmountM2(value);
        } else {
          setAmount(value);
        }
      }
      setTimeout(() => {
        //Use a timeout to let events be handled before focusing
        inputRef.current?.focus();
        if (!value) inputRef.current?.select();
      }, 1);
    }
  }

  //To call methods from outside:
  useImperativeHandle(ref, () => ({
    focusInput
  }));

  return (
    <motion.div
      role="listitem"
      data-sku={sku}
      className={classNames(
        "product-list-item",
        {
          compact: compact,
          "no-shop": !features.shop(),
          b2b: b2b && features.shop()
        },
        className
      )}
      onClick={e => {
        if (b2b && searchResultMode) {
          setActive(sku);
          focusInput();
          e.stopPropagation();
        } else {
          if (productUrl)
            navigate(productUrl, {
              state: noOverlay ? addNoOverlayState() : {}
            });
        }
      }}
      style={{
        cursor: b2b && searchResultMode ? "default" : "pointer",
        filter: "saturate(1)",
        backgroundColor: "rgba(0,0,0,0)",
        borderBottomColor: "rgba(0,0,0,0.05)"
      }}
      initial={{ opacity: 0 }}
      exit={{
        opacity: 0,
        x: "200%",
        transition: {
          type: "spring",
          duration: 1
        }
      }}
      animate={{
        backgroundColor: active ? "rgb(252, 186, 87, 1)" : "rgba(0,0,0,0)",
        x: 0,
        opacity: 1
      }}
      whileHover={{
        filter: "saturate(1.2)",
        backgroundColor: active ? "rgb(252, 186, 87, 1)" : "rgba(0,0,0,0.05)",
        borderBottomColor: "rgba(0,0,0,0.1)"
      }}
      transition={{
        type: "tween",
        duration: 0.1,
        bounce: 0
      }}
    >
      <div className={"image-col"}>
        <div className="image-container">
          <div
            className="productPreviewImage"
            style={{
              backgroundImage:
                images &&
                images[0] &&
                `url("${images[0].styles?.preview || ""}")`
            }}
          ></div>
        </div>
      </div>
      <div className={"info-col"}>
        <h4>
          {b2b && <span className="text-pale">{sku} </span>}
          <span>{name}</span>
          <Flags product={product} inline={true} />
        </h4>
        <div className="specs">
          <IconsOrTagline
            specsIndexed={iconSpecsIndexed}
            showLabel={false}
            sku={sku}
            tagline={tagline}
          />{" "}
          {b2b && searchResultMode && productUrl && (
            <Link to={productUrl}>{t("more.info")}</Link>
          )}
        </div>
      </div>

      {(features.shop() || b2b) && (
        <div className="price-col">
          {hasPrice && (
            <div className={"price"}>
              {isPerSquareMeter && !!unitPrice ? (
                <FormattedPrice
                  price={unitDisplayPrice}
                  price_unit={priceUnit}
                  currency={currency}
                  price_class_name="big-price"
                />
              ) : (
                <FormattedPrice
                  price={displayPrice}
                  sales_unit={priceUnit}
                  currency={currency}
                  price_class_name="big-price"
                />
              )}
            </div>
          )}
          <div className="stock-cell">
            <StockStatus
              sku={sku}
              stockStatus={stockStatus}
              key={"stock-status"}
            />
            {b2b && productIsPurchasable(product, b2b) && (
              <div className={"text-muted"}>
                {product.stock?.availableStock || "0"}{" "}
                {tp("sales_unit.per_package")}
              </div>
            )}
          </div>
        </div>
      )}

      {features.shop() &&
        (b2b ? (
          <div className={"cart-col"}>
            <motion.div
              className="exists-in-cart"
              animate={{
                opacity: inCart && inCart.amount ? 1 : 0,
                scale: inCart && inCart.amount ? 1 : 0.8
              }}
              transition={{ type: "spring", damping: 5 }}
            >
              {inCart && inCart.amount && (
                <>
                  <div
                    title={t("cart.existing.product", {
                      amount: inCart.amount
                    })}
                  >
                    <Icon name="cart-shopping" /> <span>{inCart.amount}</span>
                  </div>
                  <div className="text-small">
                    {" "}
                    {tp("sales_unit." + priceUnit)}
                  </div>
                </>
              )}
            </motion.div>
            <div className="amount-cell">
              {isPerSquareMeter ? (
                <PackageCalculator
                  valueM2={amountM2}
                  m2PerPackage={unitQuantityFactor || 0}
                  pricePerPackage={
                    basePrice
                      ? showPricesWithVat
                        ? addVat(parseFloat(basePrice))
                        : parseFloat(basePrice)
                      : 0
                  }
                  stock={stock?.availableStock || ""}
                  valueCallback={setAmount}
                  b2b
                  compact
                  ref={inputRef}
                  onKeyDown={e => {
                    switch (e.key) {
                      case "Escape":
                        closeParent();
                        e.preventDefault();
                        break;
                      case "Enter":
                        e.preventDefault();
                        if (addToCartHandler()) {
                          focusSearchBox();
                        }
                        break;
                      default:
                    }
                  }}
                />
              ) : (
                <>
                  <Input
                    type="text"
                    inputMode="numeric"
                    min="0"
                    max="9999"
                    value={amount}
                    style={{ width: "4rem" }}
                    innerRef={inputRef}
                    onChange={e => {
                      let newAmount = parseInt(e.target.value);
                      if (isNaN(newAmount) || newAmount <= 0) {
                        newAmount = 0;
                      }
                      setAmount(newAmount);
                    }}
                    onKeyDown={e => {
                      switch (e.key) {
                        case "Escape":
                          closeParent();
                          e.preventDefault();
                          break;
                        case "Enter":
                          e.preventDefault();
                          if (addToCartHandler()) {
                            closeParent();
                          }
                          break;
                        default:
                      }
                    }}
                    onClick={e => {
                      e.stopPropagation();
                    }}
                  />
                  <div className="text-small">
                    {tp("sales_unit." + priceUnit)}
                  </div>
                </>
              )}
            </div>
            <BjelinButton
              color={
                productIsPurchasable(product, b2b) ? "primary" : "bjelin-grey"
              }
              disabled={!productIsPurchasable(product, b2b)}
              block={false}
              lg={false}
              icon="cart-plus"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                if (amount) {
                  addToCartHandler();
                } else {
                  focusInput();
                }
              }}
              className="amount-btn"
            >
              <span className="btn-label">{tp("add_to_cart.short")}</span>
            </BjelinButton>
          </div>
        ) : showCartButton ? (
          <div className={"cart-col"}>
            <AnimatePresence>
              {inCart && inCart.amount ? (
                <motion.div
                  initial={{
                    opacity: 0,
                    scale: 0.8
                  }}
                  animate={{
                    opacity: 1,
                    scale: 1
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.8
                  }}
                  transition={{ type: "spring", damping: 5 }}
                  className="exists-in-cart"
                  title={t("cart.existing.product", {
                    amount: inCart.amount
                  })}
                >
                  <Icon name="check" /> <span>{inCart.amount}</span>
                  <span className="text-small">
                    {" "}
                    {tp("sales_unit." + priceUnit)}
                  </span>
                </motion.div>
              ) : (
                <BjelinButton
                  color={
                    productIsPurchasable(product, b2b)
                      ? "primary"
                      : "bjelin-grey"
                  }
                  disabled={!productIsPurchasable(product, b2b)}
                  block={false}
                  lg={false}
                  icon="cart-plus"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    addToCartHandler();
                  }}
                  className="amount-btn"
                >
                  <span className="btn-label">{tp("add_to_cart.short")}</span>
                </BjelinButton>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <div className="action-col">
            <BjelinButton
              color={
                productIsPurchasable(product, b2b)
                  ? "primary"
                  : "bjelin-middlegrey"
              }
              block={false}
              lg={false}
              icon="chevron-right"
              onClick={e => {
                navigate(productUrl || "");
                e.stopPropagation();
              }}
              className="info-btn"
            >
              <span className="btn-label">{upperFirst(tp("info"))}</span>
            </BjelinButton>
          </div>
        ))}
    </motion.div>
  );
});

ProductListItem.displayName = "ProductListItem";

export default ProductListItem;

export const ProductListItemPlaceholder = forwardRef<
  HTMLDivElement,
  { product: Product; compact: boolean }
>(({ product, compact }, ref) => {
  ref = ref || createRef();
  const { name, sku } = product;

  return (
    <div
      className={classNames("render-placeholder", "product-list-item", {
        compact
      })}
      data-sku={sku}
      ref={ref}
    >
      <div className={"image-col"}>
        <div className="image-container">
          <div className="productPreviewImage"></div>
        </div>
      </div>
      <div className={"info-col"}>
        <h4>{name}</h4>
      </div>
      <div className={"price-col"}></div>
    </div>
  );
});
ProductListItemPlaceholder.displayName = "ProductListItemPlaceholder";
